import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ArrowUpRight } from "lucide-react";
import { PHONE, PHONE_LINK } from "@/components/Ui";

const prestations = [
  { label: "Maçonnerie à Bernay", href: "/maconnerie-bernay" },
  { label: "Rénovation à Bernay", href: "/renovation-bernay" },
  { label: "Extension de maison", href: "/extension-maison" },
  { label: "Ouverture de mur porteur", href: "/ouverture-mur-porteur" },
  { label: "Béton & aménagements extérieurs", href: "/beton-amenagements-exterieurs" },
];

const links = [
  { label: "Réalisations", href: "/realisations" },
  { label: "Zones", href: "/zones" },
  { label: "À propos", href: "/a-propos" },
  { label: "Contact", href: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [prestationsOpen, setPrestationsOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setPrestationsOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled || pathname !== "/" ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[70px] flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src="/logo-header.png" alt="DMZ Normandie — Maçonnerie & rénovation" className="h-11 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          <Link to="/" className="text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[#F5F5F0] transition-colors duration-300" data-testid="nav-accueil">
            Accueil
          </Link>
          <div className="relative" onMouseEnter={() => setPrestationsOpen(true)} onMouseLeave={() => setPrestationsOpen(false)}>
            <button className="flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[#F5F5F0] transition-colors duration-300" data-testid="nav-prestations">
              Prestations <ChevronDown size={13} className={`transition-transform duration-300 ${prestationsOpen ? "rotate-180" : ""}`} />
            </button>
            {prestationsOpen && (
              <div className="absolute top-full left-0 pt-3 w-72" data-testid="prestations-dropdown">
                <div className="bg-[#0D0D0D] border border-white/10 shadow-2xl shadow-black/60 py-2">
                  {prestations.map((p) => (
                    <Link
                      key={p.href}
                      to={p.href}
                      className="block px-5 py-3 text-sm font-light text-zinc-400 hover:text-[#C4A484] hover:bg-white/[0.03] transition-colors"
                      data-testid={`nav-${p.href.slice(1)}`}
                    >
                      {p.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {links.map((l) => (
            <Link key={l.href} to={l.href} className="text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[#F5F5F0] transition-colors duration-300" data-testid={`nav-${l.href.slice(1)}`}>
              {l.label}
            </Link>
          ))}
          <a href={PHONE_LINK} className="flex items-center gap-2 text-xs tracking-[0.15em] text-zinc-300 hover:text-[#C4A484] transition-colors" data-testid="header-phone">
            <Phone size={13} className="text-[#C4A484]" /> {PHONE}
          </a>
          <Link
            to="/contact"
            data-testid="header-devis-cta"
            className="group flex items-center gap-2 text-xs tracking-[0.2em] uppercase border border-white/20 hover:border-[#C4A484] hover:text-[#C4A484] px-5 py-2.5 transition-colors duration-300"
          >
            Devis
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <div className="flex lg:hidden items-center gap-3">
          <a href={PHONE_LINK} className="w-10 h-10 bg-[#C4A484] text-[#0A0A0A] rounded-full flex items-center justify-center" data-testid="header-phone-mobile" aria-label="Appeler">
            <Phone size={16} />
          </a>
          <button onClick={() => setOpen(!open)} className="w-10 h-10 flex items-center justify-center text-[#F5F5F0]" data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/10 px-6 py-8 max-h-[calc(100vh-70px)] overflow-y-auto" data-testid="mobile-nav">
          <Link to="/" className="block py-3 text-sm tracking-[0.25em] uppercase text-zinc-300" data-testid="mobile-nav-accueil">Accueil</Link>
          <p className="pt-4 pb-2 text-[10px] tracking-[0.4em] uppercase text-[#C4A484]">Prestations</p>
          {prestations.map((p) => (
            <Link key={p.href} to={p.href} className="block py-2.5 pl-3 text-sm font-light text-zinc-400 border-l border-white/10" data-testid={`mobile-nav-${p.href.slice(1)}`}>
              {p.label}
            </Link>
          ))}
          {links.map((l) => (
            <Link key={l.href} to={l.href} className="block py-3 text-sm tracking-[0.25em] uppercase text-zinc-300" data-testid={`mobile-nav-${l.href.slice(1)}`}>
              {l.label}
            </Link>
          ))}
          <Link to="/contact" className="btn-sable w-full mt-4" data-testid="mobile-nav-devis">
            Demander un devis
          </Link>
        </nav>
      )}
    </header>
  );
}
